// Общие элементы графиков журнала: карточка, всплывающая подсказка, легенда-переключатель, полоса показателей
import React from 'react';
import { Alert, Box, Button, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';

export const BORDER = '#e2e8f0';

/** Свойства, которые recharts передаёт в content-элемент Tooltip */
export interface TooltipInjected<T> {
  active?: boolean;
  payload?: Array<{ payload: T }>;
  label?: string | number;
}

/** Точка данных из подсказки recharts (или null, если подсказка не активна) */
export function tooltipPoint<T>(props: TooltipInjected<T>): T | null {
  if (!props.active || !props.payload || !props.payload.length) return null;
  return props.payload[0].payload || null;
}

export interface TipRow {
  label: string;
  value: React.ReactNode;
  color?: string;
  /** Подстрочная поясняющая строка (доля, изменение) */
  hint?: React.ReactNode;
  bold?: boolean;
  divider?: boolean;
}

/** Светлая подсказка графика: заголовок + строки «показатель - значение» */
export const TipBox: React.FC<{ title: React.ReactNode; subtitle?: React.ReactNode; rows: TipRow[]; footer?: React.ReactNode; width?: number }> = ({
  title, subtitle, rows, footer, width = 250
}) => (
  <Box sx={{ bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: 2, boxShadow: '0 10px 28px rgba(15,23,42,0.12)', px: 1.5, py: 1.25, width, maxWidth: '86vw' }}>
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>{title}</Typography>
    {subtitle && <Typography sx={{ fontSize: '0.7rem', color: '#64748b', mt: 0.25 }}>{subtitle}</Typography>}
    <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
      {rows.map((r, i) => (
        <Box key={`${r.label}-${i}`} sx={r.divider ? { borderTop: `1px dashed ${BORDER}`, pt: 0.5, mt: 0.25 } : undefined}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {r.color && <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: r.color, flexShrink: 0 }} />}
            <Typography sx={{ fontSize: '0.74rem', color: '#475569', flexGrow: 1, minWidth: 0 }} noWrap>{r.label}</Typography>
            <Typography sx={{ fontSize: '0.76rem', fontWeight: r.bold ? 700 : 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{r.value}</Typography>
          </Box>
          {r.hint && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', textAlign: 'right', lineHeight: 1.2 }}>{r.hint}</Typography>}
        </Box>
      ))}
    </Box>
    {footer && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.75 }}>{footer}</Typography>}
  </Box>
);

interface CardProps {
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  children: React.ReactNode;
  /** Растянуть на всю ширину сетки */
  wide?: boolean;
}

/** Карточка графика: заголовок, действия справа, полоса загрузки, ошибка с повтором */
export const ChartCard: React.FC<CardProps> = ({ title, subtitle, actions, loading, error, onRetry, children, wide }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: BORDER, overflow: 'hidden', gridColumn: wide ? '1 / -1' : undefined, minWidth: 0 }}>
    <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
    <Box sx={{ px: 2, pt: 1.25, pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#0f172a' }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: '0.74rem', color: '#64748b', mt: 0.25 }}>{subtitle}</Typography>}
      </Box>
      {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>{actions}</Box>}
    </Box>
    <Box sx={{ px: 2, pb: 2, opacity: loading ? 0.65 : 1, transition: 'opacity 0.15s' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Повторить</Button> : undefined}>
          {error}
        </Alert>
      )}
      {children}
    </Box>
  </Paper>
);

/** Пустое состояние графика */
export const NoData: React.FC<{ height?: number; text?: string }> = ({ height = 240, text = 'Нет данных по выбранным фильтрам' }) => (
  <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontSize: '0.85rem', bgcolor: '#fafbfc', borderRadius: 1.5, border: `1px dashed ${BORDER}` }}>
    {text}
  </Box>
);

/** Скелет графика до первого ответа */
export const ChartSkeleton: React.FC<{ height?: number }> = ({ height = 280 }) => (
  <Box sx={{ height, borderRadius: 1.5, bgcolor: '#f8fafc', border: `1px solid ${BORDER}` }} />
);

export interface SeriesToggle {
  key: string;
  label: string;
  color: string;
  /** Пунктир - для рядов сравнения и линий */
  dashed?: boolean;
  line?: boolean;
}

/** Легенда-переключатель: клик скрывает / показывает ряд */
export const SeriesLegend: React.FC<{ items: SeriesToggle[]; hidden: string[]; onToggle: (key: string) => void }> = ({ items, hidden, onToggle }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
    {items.map(s => {
      const off = hidden.indexOf(s.key) !== -1;
      return (
        <Box
          key={s.key} component="button" type="button" onClick={() => onToggle(s.key)}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.35, borderRadius: 1.5, cursor: 'pointer',
            border: `1px solid ${off ? BORDER : 'transparent'}`, bgcolor: off ? 'transparent' : '#f8fafc', font: 'inherit',
            color: off ? '#94a3b8' : '#334155', fontSize: '0.74rem', fontWeight: 500,
            '&:hover': { bgcolor: '#f1f5f9' }
          }}
        >
          {s.line
            ? <Box sx={{ width: 14, height: 0, borderTop: `2px ${s.dashed ? 'dashed' : 'solid'} ${off ? '#cbd5e1' : s.color}` }} />
            : <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: off ? '#e2e8f0' : s.color }} />}
          {s.label}
        </Box>
      );
    })}
  </Box>
);

export interface Stat {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  color?: string;
  tooltip?: string;
}

/** Полоса итоговых показателей под / над графиком */
export const StatStrip: React.FC<{ items: Stat[] }> = ({ items }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1, mb: 1.5 }}>
    {items.map(s => {
      const body = (
        <Box sx={{ px: 1.25, py: 0.9, borderRadius: 1.5, bgcolor: '#f8fafc', border: `1px solid #eef2f7`, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }} noWrap>{s.label}</Typography>
          <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: s.color || '#0f172a', lineHeight: 1.35 }} noWrap>{s.value}</Typography>
          {s.hint && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }} noWrap>{s.hint}</Typography>}
        </Box>
      );
      return s.tooltip ? <Tooltip key={s.label} title={s.tooltip} arrow>{body}</Tooltip> : <React.Fragment key={s.label}>{body}</React.Fragment>;
    })}
  </Box>
);

/** Горизонтальная шкала доли (для таблиц аналитики) */
export const ShareBar: React.FC<{ value: number; max?: number; color?: string; label?: string }> = ({ value, max = 100, color = '#2563eb', label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 90 }}>
    <Box sx={{ flexGrow: 1, height: 6, borderRadius: 3, bgcolor: '#eef2f7', overflow: 'hidden' }}>
      <Box sx={{ width: `${Math.max(0, Math.min(100, max ? (value / max) * 100 : 0))}%`, height: '100%', bgcolor: color, borderRadius: 3 }} />
    </Box>
    {label !== undefined && <Typography sx={{ fontSize: '0.72rem', color: '#475569', minWidth: 40, textAlign: 'right' }}>{label}</Typography>}
  </Box>
);

/** Общие свойства осей */
export const AXIS_TICK = { fontSize: 11, fill: '#64748b' };

/** Компактные таблицы аналитики: светлая шапка, плотные строки, лёгкий hover */
export const TABLE_SX = {
  '& th': { fontSize: '0.72rem', fontWeight: 600, color: '#64748b', bgcolor: '#f8fafc', whiteSpace: 'nowrap', py: 0.75, px: 1, borderColor: BORDER, lineHeight: 1.25 },
  '& td': { fontSize: '0.78rem', color: '#0f172a', py: 0.6, px: 1, borderColor: '#f1f5f9' },
  '& tbody tr:hover td': { bgcolor: '#f8fafc' }
} as const;

/** Обрезка длинной подписи оси с многоточием */
export const clip = (s: string, max: number): string => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

/** Подпись под значением в ячейке таблицы */
export const SubText: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = '#94a3b8' }) => (
  <Typography component="span" sx={{ display: 'block', fontSize: '0.68rem', color, lineHeight: 1.2 }} noWrap>{children}</Typography>
);
